﻿import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  GetAgencyList,
  GetAgencyDetails,
  UpdateEnabled,
  UpdateConvention,
  GenerateCode,
} from '../../../api/admin';

export default function AgencyMgt() {
  const [loading, setLoading] = useState(true);
  const [selectedAgency, setAgency] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('0');
  const [selectedTextFilter, setSelectedTextFilter] = useState('');
  const [agencies, setAgencies] = useState(null);
  const [filteredAgencies, setFilteredAgencies] = useState(null);
  const [agencyID, setAgencyID] = useState(null);
  const [profileID, setProfileID] = useState(null);
  const [selectedEnabledValue, setSelectedEnabledValue] = useState(null);
  const [selectedConventionValue, setSelectedConventionValue] = useState(null);

  const shortSpan = {
    width: '250px',
    display: 'inline',
  };

  const LoadAgencies = useQuery({
    queryKey: ['getAgencyList', selectedAgency?.AgencyID || 0],
    queryFn: async () => await GetAgencyList()(),
    enabled: false,
  });

  // Handle LoadAgencies success
  useEffect(() => {
    if (LoadAgencies.isSuccess && LoadAgencies.data) {
      setAgencies(JSON.parse(LoadAgencies.data));
      setSelectedTextFilter(selectedTextFilter);
      setLoading(false);
    }
  }, [LoadAgencies.isSuccess, LoadAgencies.data]);

  useEffect(() => {
    LoadAgencies.refetch();
    setSelectedFilter('0');
    setSelectedTextFilter('');
  }, []);

  useEffect(() => {
    if (agencies !== null) {
      let agts = agencies;

      if (selectedFilter !== '0') {
        agts = agts.filter((f) => f.EnabledYN.toString() === selectedFilter);
      }

      if (selectedTextFilter !== '') {
        agts = agts.filter(
          (f) => f.AgencyDisplayName.toLowerCase().indexOf(selectedTextFilter.toLowerCase()) !== -1
        );
      }

      setFilteredAgencies(agts);
    }
  }, [selectedFilter, selectedTextFilter, agencies]);

  const getAgencyDetails = useQuery({
    queryKey: ['getAgencyDetails', agencyID || 0],
    queryFn: async () => await GetAgencyDetails({ AgencyID: agencyID })(),
    enabled: agencyID != null,
  });

  // Handle getAgencyDetails success
  useEffect(() => {
    if (getAgencyDetails.isSuccess && getAgencyDetails.data) {
      setAgency(JSON.parse(getAgencyDetails.data));
    }
  }, [getAgencyDetails.isSuccess, getAgencyDetails.data]);

  const generateCode = useQuery({
    queryKey: ['generateCode', profileID, selectedAgency?.AgencyID || 0],
    queryFn: async () =>
      await GenerateCode({ ProfileID: profileID, AgencyID: selectedAgency?.AgencyID || 0 })(),
    enabled: false,
  });

  // Handle generateCode success
  useEffect(() => {
    if (generateCode.isSuccess) {
      setProfileID(null);
    }
  }, [generateCode.isSuccess]);

  useEffect(() => {
    if (profileID != null) {
      generateCode.refetch();
    }
  }, [profileID, selectedAgency]);

  const updateEnabled = useQuery({
    queryKey: ['updateEnabled', selectedAgency?.AgencyID || 0, selectedEnabledValue],
    queryFn: async () =>
      await UpdateEnabled({
        EnabledYesNo: selectedEnabledValue,
        AgencyID: selectedAgency?.AgencyID || 0,
      })(),
    enabled: selectedEnabledValue != null && selectedAgency != null,
  });

  // Handle updateEnabled success
  useEffect(() => {
    if (updateEnabled.isSuccess) {
      setAgencyID(selectedAgency.AgencyID);
      LoadAgencies.refetch();
      setSelectedEnabledValue(null);
    }
  }, [updateEnabled.isSuccess]);

  const updateConvention = useQuery({
    queryKey: ['updateConvention', selectedAgency?.AgencyID || 0, selectedConventionValue],
    queryFn: async () =>
      await UpdateConvention({
        EnabledYesNo: selectedConventionValue,
        AgencyID: selectedAgency?.AgencyID || 0,
      })(),
    enabled: false,
  });

  // Handle updateConvention success
  useEffect(() => {
    if (updateConvention.isSuccess) {
      setAgencyID(selectedAgency.AgencyID);
      LoadAgencies.refetch();
      setSelectedConventionValue(null);
    }
  }, [updateConvention.isSuccess]);

  const UpdateConvention = (val) => {
    setSelectedConventionValue(val);
    updateConvention.refetch();
  };

  const DisplayTable = () => {
    return (
      <div className="container">
        <div className="row">
          <div className="col"></div>
        </div>
        <div className="row">
          <div className="col text-right">
            <span style={shortSpan}>
              Filter:&nbsp;
              <select
                id="agencyFilter"
                className="form-control"
                style={{ display: 'inline', width: '200px' }}
                onChange={(e) => setSelectedFilter(e.target.value)}
              >
                <option value="0">All</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </span>
          </div>
        </div>
        <div className="row">
          <div className="col text-right">
            <span style={shortSpan}>
              Text Filter:&nbsp;
              <input
                type="text"
                id="agencyTextFilter"
                className="form-control"
                style={{ display: 'inline', width: '200px' }}
                onBlur={(e) => setSelectedTextFilter(e.target.value)}
              />
            </span>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Agency Name</th>
                  <th>Enabled</th>
                </tr>
              </thead>
              <tbody>
                {filteredAgencies?.map((e, i) => {
                  return (
                    <tr key={i + 'row'} onClick={() => setAgencyID(e.ID)}>
                      <td>{e.AgencyDisplayName}</td>
                      <td>{e.EnabledYN}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const DisplayAgency = () => {
    return (
      <div className="container">
        <div className="row">
          <div className="col">&nbsp;</div>
        </div>
        <div className="row">
          <div className="col">
            <strong>Agency Name: </strong>
            {selectedAgency.AgencyName}
          </div>
          <div className="col text-right">
            <button
              type="button"
              className="btn btn-default"
              onClick={() => {
                setAgency(null);
              }}
            >
              Back to List
            </button>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col">
            <div className="container">
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label>Enabled:</label>
                    <select
                      value={selectedAgency.AgencyEnabledYN}
                      onChange={(e) => {
                        setSelectedEnabledValue(e.target.value);
                      }}
                      className="form-control"
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                  <div className="form-group">
                    Changing the enabled field will save the value immediately.
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label>Convention Enabled:</label>
                    <select
                      value={selectedAgency.AgencyConventionYN}
                      onChange={(e) => {
                        UpdateConvention(e.target.value);
                      }}
                      className="form-control"
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                  <div className="form-group">
                    Changing the enabled field will change the value immediately.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Profile Name</th>
                  <th>Registration Code</th>
                </tr>
              </thead>
              <tbody>
                {selectedAgency.Codes?.map((e, i) => {
                  return (
                    <tr key={'code' + i}>
                      <td>{e.ProfileName}</td>
                      <td>
                        {e.Code === null ? (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                              setProfileID(e.ProfileID);
                              setAgencyID(selectedAgency.AgencyID);
                            }}
                          >
                            Generate Code
                          </button>
                        ) : (
                          e.Code
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {loading ? <div>Loading...</div> : selectedAgency === null ? DisplayTable() : DisplayAgency()}
    </>
  );
}
